import React, { RefObject } from 'react';
import { PersonIcon, HamburgerMenuIcon } from '@radix-ui/react-icons';
import { useSelector, useDispatch } from 'react-redux';
import { authSelector } from '../../features/auth/authSlice';
import { GalleryMode } from '../PromptGallery/data/galleryTypes';
import { useTheme } from '../Theme/ThemeProvider';
import { ChatMode, setChatMode, setRagChatType, setSelectedTool } from '../../store/chatModeSlice';
import { RootState } from '../../store/store';
import { clearUploadedFiles } from '../../store/chatSlice';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { useState } from 'react';
import { LogoWithXAxisCircle } from '../Statistics/LogoWithXAxisCircle';


interface HeaderProps {
  mode: "light" | "dark";
  galleryState: {
    current: { mode: GalleryMode | null };
    goToModeWithDirection: (mode: GalleryMode, direction: number) => void;
    exitDirectionRef: RefObject<number>;
    setExitDirectionRef: RefObject<((direction: number) => void) | null>;
    isFirstAnimationRunning: boolean;
  };
  onMenuToggle?: () => void;
  isMobileOrTablet?: boolean;
  currentPage?: 'main' | 'chat';
  onNavigateHome?: () => void;
  isStreaming?: boolean;
  isVoiceModeActive?: boolean;
  hasMessages?: boolean;
  onStatsClick?: () => void;
}

export function Header({ mode, galleryState, onMenuToggle, isMobileOrTablet = false, currentPage = 'main', onNavigateHome, isStreaming = false, isVoiceModeActive = false, hasMessages = false, onStatsClick }: HeaderProps) {
  const auth = useSelector(authSelector);
  const dispatch = useDispatch();
  const { accent } = useTheme();
  const chatModeState = useSelector((state: RootState) => state.chatMode);
  const uploadedFiles = useSelector((state: RootState) => state.chat.uploadedFiles);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingMode, setPendingMode] = useState<ChatMode | null>(null);
  const [showRagHomeConfirmation, setShowRagHomeConfirmation] = useState(false);

  const handleLogoClick = () => {
    if (isStreaming) return; // Prevent navigation while streaming
    // Always clear selected tool when navigating home
    dispatch(setSelectedTool(null));
    if (currentPage !== 'main' && onNavigateHome) {
      onNavigateHome();
    }
  };

  const handleChatModeChange = (newMode: ChatMode) => {
    if (newMode === 'Ask Chat') {
      dispatch(setChatMode(newMode));
      // On the main page a RAG may have been selected from the selector but the user
      // never actually entered a chat — reset it so returning to 3M Knowledge shows
      // the selector again. On the chat page, preserve it so switching back restores
      // the active RAG mid-conversation.
      if (currentPage === 'main') {
        dispatch(setRagChatType(null));
      }
      return;
    }

    if (newMode === '3M Knowledge') {
      // Scenario 2: On chat page with NO RAG selected — warn that chat will be cleared,
      // then navigate home (which is where the RAG selector lives).
      // Skip dialog if there's no history to lose.
      if (currentPage === 'chat' && chatModeState.ragChatType === null) {
        if (!hasMessages) {
          if (onNavigateHome) onNavigateHome();
          dispatch(setChatMode('3M Knowledge'));
        } else {
          setShowRagHomeConfirmation(true);
        }
        return;
      }

      // Scenario 4: On chat page with a RAG selected — just switch mode back, no navigation.
      // Scenario 1: On main page — just switch mode (ragChatType already null).
      // Uploaded files check applies in all remaining cases.
      if (uploadedFiles.length > 0) {
        setPendingMode(newMode);
        setShowConfirmation(true);
        return;
      }

      dispatch(setChatMode(newMode));
    }
  };

  const handleConfirmModeChange = () => {
    if (pendingMode) {
      dispatch(clearUploadedFiles());
      dispatch(setChatMode(pendingMode));
      setShowConfirmation(false);
      setPendingMode(null);
    }
  };

  const handleConfirmRagHome = () => {
    // Scenario 2 confirmed: navigate home (clears chat history, resets ragChatType to null)
    setShowRagHomeConfirmation(false);
    if (onNavigateHome) onNavigateHome();
    dispatch(setChatMode('3M Knowledge'));
  };

  const handleCancelRagHome = () => {
    setShowRagHomeConfirmation(false);
  };

  const handleCancelModeChange = () => {
    setShowConfirmation(false);
    setPendingMode(null);
  };

  // Map current accent to match the main content colors in dark mode
  // These should be the same colors used in the main content area for consistency
  const getLightAccentTextColor = (accentColor: string) => {
    if (accentColor === 'purple') return '#a78bfa'; // purple-400 - matches dark mode main content
    if (accentColor === 'blue') return '#60a5fa'; // blue-400 - matches dark mode main content  
    if (accentColor === 'green') return '#34d399'; // emerald-400 - matches dark mode main content
    return '#e5e7eb'; // gray-200 for none themes
  };

  // Get gradient color based on accent theme
  const getGradientColor = (accentColor: string) => {
    if (accentColor === 'purple') return '#8b5cf6'; // purple-500
    if (accentColor === 'blue') return '#3b82f6'; // blue-500
    if (accentColor === 'green') return '#10b981'; // emerald-500
    if (accentColor === 'gold') return '#f59e0b'; // amber-500
    return '#D10411'; // red as default for 'none' themes
  };

  // Convert hex color to RGB values for gradient
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 209, g: 4, b: 17 }; // fallback to red
  };

  const textColor = getLightAccentTextColor(accent);
  const gradientColor = getGradientColor(accent);
  const rgbColor = hexToRgb(gradientColor);

  const getToggleStyle = (isActive: boolean): React.CSSProperties => ({
    fontSize: 'clamp(0.75rem, 1.2vw, 1.1rem)',
    paddingTop: '6px',
    paddingBottom: '5px',
    minWidth: '150px',
    textAlign: 'center',
    background: isActive
      ? 'linear-gradient(160deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 40%, rgba(0,0,0,0.1) 100%)'
      : 'linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)',
    backdropFilter: isActive ? 'blur(12px) saturate(1.4)' : 'blur(6px)',
    WebkitBackdropFilter: isActive ? 'blur(12px) saturate(1.4)' : 'blur(6px)',
    border: isActive
      ? '1px solid rgba(255,255,255,0.3)'
      : '1px solid rgba(255,255,255,0.06)',
    borderTop: isActive ? '1px solid rgba(255,255,255,0.45)' : '1px solid rgba(255,255,255,0.1)',
    borderBottom: isActive ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.03)',
    boxShadow: isActive
      ? '0 4px 16px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2), inset 0 1px 2px rgba(255,255,255,0.15), inset 0 -1px 2px rgba(0,0,0,0.1)'
      : '0 1px 4px rgba(0,0,0,0.2), inset 0 1px 1px rgba(255,255,255,0.03)',
    color: isActive ? '#ffffff' : 'rgba(255,255,255,0.45)',
    textShadow: isActive ? '0 1px 3px rgba(0,0,0,0.4)' : 'none',
    fontWeight: isActive ? 700 : 500,
  });

  return (
    <header
      className="w-full px-4 flex items-center z-[110] fixed top-0 left-0 right-0 overflow-hidden"
      style={{
        backgroundColor: 'var(--color-header-bg)',
        height: '80px',
      }}
    >
      {/* Radial gradient bottom border */}
      <div 
        className="absolute bottom-0 left-0 w-full h-1"
        style={{
          background: `radial-gradient(ellipse at center, ${gradientColor} 0%, ${gradientColor} 40%, rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, 0.3) 60%, rgba(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}, 0) 80%)`,
        }}
      />
      
      {/* Mobile/Tablet Layout: Menu (left) - Logo (center) - User (right) */}
      {isMobileOrTablet ? (
        <>
          {/* Hamburger menu on far left */}
          <div className="flex items-center relative">
            {onMenuToggle && (
              <button
                aria-label="Menu"
                onClick={onMenuToggle}
                className="p-2 rounded-full hover:bg-[var(--color-accent)]/10 focus:outline-none"
                style={{ color: textColor }}
                data-tutorial="mobile-menu-button"
              >
                <HamburgerMenuIcon width={24} height={24} />
              </button>
            )}
          </div>
          
          {/* Centered logo */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2">
            <img
              src="/logo-dark.png"
              alt="Logo"
              className={`h-10 ${currentPage === 'main' ? 'cursor-default' : isStreaming ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-80 transition-opacity'}`}
              onClick={handleLogoClick}
            />
          </div>
          {/* Mode toggle buttons hidden on mobile/tablet */}
          
          {/* User profile on far right, logo to its left */}
          <div className="flex items-center ml-auto gap-2 relative">
            <div
              aria-label="User Profile"
              className="rounded-full border focus:outline-none flex items-center justify-center overflow-hidden"
              style={{ color: textColor, borderColor: textColor, width: 36, height: 36, cursor: 'default' }}
            >
              {auth.photo ? (
                <img
                  alt={auth.profile ? auth.profile.name : 'UserName'}
                  src={auth.photo}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className="p-1 flex items-center justify-center">
                  <PersonIcon width={28} height={28} />
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* Desktop Layout: Logo (left) - Buttons (center) - User (right) */
        <div className="flex items-center justify-between w-full gap-4 relative">
          <div className="flex items-center">
            <img
              src="/logo-dark.png"
              alt="Logo"
              className={`h-10 ${currentPage === 'main' ? 'cursor-default' : isStreaming ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:opacity-80 transition-opacity'}`}
              onClick={handleLogoClick}
            />
          </div>
          {/* Mode toggle buttons on desktop - centered in available space */}
          <div className="flex items-center justify-center flex-1 gap-2">
            <span
              className={`drop-shadow-md transition-all duration-200 rounded-xl relative overflow-visible transform select-none font-semibold px-4 ${
                chatModeState.mode !== 'Ask Chat' && !isStreaming ? 'cursor-pointer hover:scale-105 hover:-translate-y-1' : isStreaming ? 'cursor-not-allowed' : ''
              } ${isStreaming ? "opacity-50" : "opacity-100"}`}
              style={getToggleStyle(chatModeState.mode === 'Ask Chat')}
              onClick={() => {
                if (chatModeState.mode === 'Ask Chat' || isStreaming) return;
                handleChatModeChange('Ask Chat');
              }}
              aria-disabled={chatModeState.mode === 'Ask Chat' || isStreaming}
            >
              Chat
            </span>
            <span
              className={`drop-shadow-md transition-all duration-200 rounded-xl relative overflow-visible transform select-none font-semibold px-4 ${
                isStreaming ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-105 hover:-translate-y-1'
              } ${isStreaming ? "opacity-50" : "opacity-100"}`}
              style={getToggleStyle(chatModeState.mode === '3M Knowledge')}
              onClick={() => {
                if (isStreaming) return;
                if (chatModeState.mode === '3M Knowledge') {
                  // Already on 3M Knowledge — bring user back to the RAG selector:
                  // - Chat page: navigate home (clears chat, returns to selector).
                  // - Main page with a RAG selected: just clear the RAG to show selector.
                  // - Main page with no RAG (selector already shown): no-op.
                  if (currentPage === 'chat') {
                    if (onNavigateHome) onNavigateHome();
                  } else if (chatModeState.ragChatType !== null) {
                    dispatch(setRagChatType(null));
                  }
                  return;
                }
                handleChatModeChange('3M Knowledge');
              }}
              aria-disabled={isStreaming}
              data-tutorial="knowledge-tab"
            >
              3M Knowledge
            </span>
          </div> 
          
          <div className="flex items-center gap-2 relative">
            {/* STATISTICS button */}
            <LogoWithXAxisCircle color={textColor} size={36} onClick={onStatsClick} active={typeof window !== 'undefined' && window.__SHOW_STATS_PANEL__ ? window.__SHOW_STATS_PANEL__ : false} data-tutorial="stats-button" />
            {/* User Profile Button with circle */}
            <div
              aria-label="User Profile"
              className="rounded-full border focus:outline-none flex items-center justify-center overflow-hidden"
              style={{ color: textColor, borderColor: textColor, width: 36, height: 36 }}
            >
              {auth.photo ? (
                <img
                  alt={auth.profile ? auth.profile.name : 'UserName'}
                  src={auth.photo}
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className="p-1 flex items-center justify-center">
                  <PersonIcon width={28} height={28} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirmation dialog for switching to 3M Knowledge with files */}
      <ConfirmationDialog
        isOpen={showConfirmation}
        title="Switch to 3M Knowledge?"
        message="Switching to 3M Knowledge will remove all uploaded files. Are you sure you want to continue?"
        onConfirm={handleConfirmModeChange}
        onCancel={handleCancelModeChange}
      />

      {/* Confirmation dialog for Scenario 2: navigating to RAG selector clears current chat */}
      <ConfirmationDialog
        isOpen={showRagHomeConfirmation}
        title="Go to 3M Knowledge?"
        message="This will clear your current chat and take you to the 3M Knowledge selector. Are you sure?"
        onConfirm={handleConfirmRagHome}
        onCancel={handleCancelRagHome}
      />
    </header>
  );
}     
